import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { ShieldIcon, CloseIcon } from '../components/Icons';
import toast from 'react-hot-toast';
import './Events.css';

function formatDate(start, end, isMulti) {
  if (!start) return '—';
  const s = new Date(start);
  if (!isMulti || !end) return s.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const e = new Date(end);
  if (s.toDateString() === e.toDateString()) return s.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()} - ${e.getDate()} ${s.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

function EscalateModal({ event, onClose, onEscalate }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return toast.error('Please provide a reason');
    setLoading(true);
    try {
      await api.post(`/events/${event._id}/escalate`, { reason });
      toast.success('Event escalated successfully');
      onEscalate();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to escalate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal" style={{ maxWidth: '32rem' }}>
        <div className="modal-header">
          <div className="modal-title" style={{ color: '#c2410c' }}>Escalate Event</div>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
              Provide a reason for escalation (e.g. Venue not available, timing conflict). This will be shown to the host.
            </div>
            <div className="field">
              <label style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', color: '#94a3b8' }}>Reason *</label>
              <textarea
                required
                rows={4}
                className="form-control"
                placeholder="Explain why this event needs attention..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={{ borderRadius: '12px', padding: '0.75rem', fontSize: '0.9375rem' }}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-navy" disabled={loading} style={{ background: '#c2410c' }}>
              {loading ? 'Processing...' : 'Confirm Escalation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Approvals() {
  const { faculty } = useAuth();
  const navigate = useNavigate();
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [escalatingEvent, setEscalatingEvent] = useState(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('pending');

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/events/all');
      setAllEvents(res.data.events || []);
    } catch (err) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleApprove = async (eventId) => {
    try {
      await api.post(`/events/${eventId}/approve`);
      toast.success('Event approved');
      loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Approval failed');
    }
  };

  const pendingEvents = allEvents.filter(e => !e.isApproved);
  const approvedEvents = allEvents.filter(e => e.isApproved);

  const currentList = activeTab === 'pending' ? pendingEvents : approvedEvents;

  const filtered = currentList.filter(ev => {
    const s = search.toLowerCase();
    return (
      ev.title.toLowerCase().includes(s) ||
      ev.venue?.toLowerCase().includes(s) ||
      (ev.faculty?.name || '').toLowerCase().includes(s)
    );
  });

  if (loading) return <div className="page-loading"><div className="spinner" /></div>;

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">Event Approvals</div>
          <div className="page-subtitle">Review and authorize campus events</div>
        </div>
        <div className="page-actions" style={{ color: '#64748b', fontSize: '0.875rem', fontWeight: 600 }}>
          {pendingEvents.length} Pending review
        </div>
      </div>

      <div className="page-body">
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '2rem', borderBottom: '1px solid var(--border-1)', marginBottom: '1.5rem' }}>
          {[
            { id: 'pending', label: `Pending (${pendingEvents.length})` },
            { id: 'approved', label: `Approved (${approvedEvents.length})` }
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: '0.75rem 0.25rem',
                fontSize: '0.9375rem',
                fontWeight: activeTab === t.id ? 800 : 600,
                color: activeTab === t.id ? 'var(--navy)' : 'var(--text-3)',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s'
              }}
            >
              {t.label}
              {activeTab === t.id && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: 'var(--navy)', borderRadius: '3px 3px 0 0' }} />
              )}
            </button>
          ))}
        </div>

        {/* Search Toolbar */}
        <div className="card toolbar" style={{ marginBottom: '1.25rem' }}>
          <input
            className="toolbar-input"
            placeholder="Search by event title, venue, or faculty name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {filtered.length === 0 ? (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '3rem 2rem', textAlign: 'center', color: 'var(--text-3)' }}>
              <div style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                {search ? 'No matching records' : `No ${activeTab} approvals`}
              </div>
              <div style={{ fontSize: '0.9375rem', marginTop: '0.5rem' }}>
                {search ? 'Try adjusting your search terms' : activeTab === 'pending' ? 'All caught up! No requests pending review.' : 'No events have been approved yet.'}
              </div>
            </div>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {filtered.map((ev, idx) => (
              <div key={ev._id} style={{ 
                padding: '1.25rem 1.5rem', 
                borderBottom: idx < filtered.length - 1 ? '1px solid var(--border-1)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '2rem'
              }}>
                <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', flex: 1 }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: ev.isApproved ? '#ecfdf5' : '#eff6ff', color: ev.isApproved ? '#059669' : '#005fa7', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: `1px solid ${ev.isApproved ? '#10b981' : '#dbeafe'}` }}>
                    <div style={{ fontSize: '18px', fontWeight: 800 }}>{new Date(ev.date).getDate()}</div>
                    <div style={{ fontSize: '10px', fontWeight: 700 }}>{new Date(ev.date).toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '1.0625rem', color: 'var(--text-1)' }}>{ev.title}</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-3)', display: 'flex', gap: '0.75rem', marginTop: '0.25rem' }}>
                      <span style={{ color: ev.isApproved ? '#059669' : '#005fa7', fontWeight: 700 }}>{formatDate(ev.date, ev.endDate, ev.isMultiDay)}</span>
                      <span className="dot">·</span>
                      <span>{ev.venue}</span>
                      <span className="dot">·</span>
                      <span>By: {ev.faculty?.name || 'Faculty'}</span>
                      {ev.isEscalated && (
                        <>
                          <span className="dot">·</span>
                          <span style={{ color: '#c2410c', fontWeight: 700 }}>Escalated</span>
                        </>
                      )}
                    </div>
                    {ev.isEscalated && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#9a3412', background: '#fff7ed', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #ffedd5' }}>
                        <strong>Reason:</strong> {ev.escalationReason}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {activeTab === 'pending' && (
                    <>
                      {ev.isEscalated && (
                        <button type="button" className="btn btn-navy btn-sm" onClick={() => navigate('/escalations')} style={{ background: '#0f172a' }}>
                          Open Chat
                        </button>
                      )}
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEscalatingEvent(ev)} style={{ color: '#c2410c' }}>
                        Escalate
                      </button>
                      <button type="button" className="btn btn-navy btn-sm" onClick={() => handleApprove(ev._id)}>
                        Approve
                      </button>
                    </>
                  )}
                  {activeTab === 'approved' && (
                    <div style={{ color: '#059669', fontSize: '0.8125rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <span>✓ Approved</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {escalatingEvent && (
        <EscalateModal
          event={escalatingEvent}
          onClose={() => setEscalatingEvent(null)}
          onEscalate={() => { setEscalatingEvent(null); loadEvents(); }}
        />
      )}
    </div>
  );
}
